<template>
  <k-panel title="Evidence" :hasContentToggle="false">
    <template #body>
      <div class="project-evidence-table">
        <div class="evidence-header">
          <div class="evidence-header-left">
            <p>Add evidence to your project using the 'Add New Evidence' button.</p>
          </div>
          <button class="btn btn-success add-evidence-btn" @click="createNewEvidence" :disabled="addEvidenceDisabled">
            Add New Evidence
          </button>
        </div>

        <div class="table-container">
          <k-table v-if="evidenceData.length > 0"
            :headers="tableHeaders"
            :rows="tableRows"
            :hideable="false"
            :downloadable="false"
            :panel="false"
            :max="10"
            @clicked="handleTableAction"
            class="evidence-table"
          />
          <empty-placeholder v-else title="No evidence found" info="You can submit a new evidence by clicking Add button on the right side.">
          </empty-placeholder>
        </div>

        <add-edit-project-evidence
          :show-modal="showEvidenceModal"
          :programme-id="programmeId"
          :project-id="projectId"
          :evidence-id="evidenceId"
          :modal-mode="modalMode"
          :evidence-data="selectedEvidenceData"
          @update="handleEvidenceUpdate"
          @close="resetModalState"
        >
        </add-edit-project-evidence>
        <view-evidence-feedback
          v-if="showFeedbackModal"
          :show-modal="showFeedbackModal"
          :evidence-data="selectedEvidenceData"
          @close="showFeedbackModal = false"
        />
      </div>
    </template>
  </k-panel>
</template>
<style scoped>
.project-evidence-table {
  padding: 15px 0;
}

.evidence-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.evidence-header-left p {
  margin: 0;
  color: var(--kate-type-primary, #6c757d);
  font-size: 0.9rem;
}

.table-container {
  overflow-x: auto;
}

.evidence-table .table {
  margin-bottom: 0;
}

</style>
<style>
.evidence-table .competency-pill {
  display: inline-block;
  font-size: 0.9em;
  letter-spacing: 0.4px;
  border-radius: 15px;
  line-height: inherit;
  padding: 3px 10px;
  margin: 2px 5px 2px 0;
}

.evidence-table .competency-pill.approved {
  color: #000;
  background-color: var(--kate-success);
}

.evidence-table .competency-pill.pending {
  background-color: var(--kate-button-outlined-hover);
  color: var(--kate-type-dark);
}

[data-theme="light"] .evidence-table .competency-pill.pending {
  border: 1px solid var(--kate-type-dark);
}

.approval-icon {
  font-size: 1.4rem;
  font-weight: bold;
  display: inline-block;
  width: 24px;
  height: 24px;
  text-align: center;
  line-height: 24px;
}

.approval-icon.approved {
  color: var(--kate-success);
}

.approval-icon.neutral {
  color: var(--kate-type-alt) !important;
}

.approval-icon.rejected {
  color: var(--kate-danger, #dc3545);
}

.evidence-table .k-table-cell:has(.approval-icon) {
  text-align: center;
}

.evidence-download-btn i {
  margin-right: 4px;
  font-size: 12px;
}
</style>
<script>
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import AddEditProjectEvidence from './add-edit-project-evidence.vue';
import ViewEvidenceFeedback from './view-evidence-feedback.vue';

export default {
  name: 'ProjectEvidenceTable',
  components: {
    KPanel,
    KTable,
    EmptyPlaceholder,
    AddEditProjectEvidence,
    ViewEvidenceFeedback,
  },

  props: {
    programmeId: {
      type: Number,
      required: true,
    },
    projectId: {
      type: Number,
      required: true,
    },
    evidenceData: {
      type: Array,
      default: () => [],
    },
    addEvidenceDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showEvidenceModal: false,
      showFeedbackModal: false,
      modalMode: undefined, // 'create', 'edit/view'
      evidenceId: undefined,
      selectedEvidenceData: null,
    };
  },

  computed: {
    tableHeaders() {
      return {
        name: {
          name: 'Name',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        description: {
          name: 'Description',
          type: 'string',
          filterable: true,
        },
        competencies: {
          name: 'Competencies',
          type: 'function',
          displayAs: row => {
            if (!row.competencies || row.competencies.length === 0) return '-';
            return row.competencies.map(comp => {
              const pillClass = comp.status ? 'approved' : 'pending';
              return `<span class="competency-pill ${pillClass}">${comp.competency_name}</span>`;
            }).join(' ');
          },
        },
        evidenceUploaded: {
          name: 'Evidence Uploaded',
          type: 'button',
          options: row => this.getEvidenceDownloadBtnConfig(row),
        },
        latestMentorComments: {
          name: 'Latest Mentor Review',
          type: 'button',
          options: row => this.getCommentsDownloadBtnConfig(row),
        },
        editEvidence: {
          name: 'Edit Evidence',
          type: 'button',
          options: {
            preset: 'edit',
            title: evidence => `Edit ${evidence.name}`,
            ariaLabel: 'Edit evidence',
          },
        },
        viewFeedback: {
          name: 'View feedback',
          type: 'button',
          options: {
            preset: 'view',
            title: evidence => `View feedback for ${evidence.name}`,
            ariaLabel: 'View feedback',
          },
        },
        approved: {
          name: 'Approved',
          type: 'function',
          displayAs: row => {
            if (row.approved === true) {
              return '<i class="fas fa-check approval-icon approved" aria-hidden="true"></i>';
            } if (row.approved === null || row.approved === undefined) {
              return '<i class="fas fa-times approval-icon neutral" aria-hidden="true"></i>';
            }
            return '<i class="fas fa-times approval-icon rejected" aria-hidden="true"></i>';
          },
        },
      };
    },

    tableRows() {
      return this.evidenceData.map((evidence, index) => ({
        id: evidence.project_evidence_id,
        name: evidence.name,
        description: evidence.description,
        competencies: evidence.competency_mappings || [],
        evidenceUploaded: {
          evidenceLink: evidence.evidence_link,
          signedUrl: evidence.signed_url,
          fileName: evidence.filename,
        },
        latestMentorComments: {
          signedUrl: evidence.mentor_commented_document_signed_url,
          fileName: evidence.mentor_commented_document_filename,
        },
        approved: evidence.approved,
        index,
      }));
    },
  },

  methods: {
    resetModalState() {
      this.showEvidenceModal = false;
      this.modalMode = undefined;
      this.evidenceId = undefined;
      this.selectedEvidenceData = null;
    },

    createNewEvidence() {
      this.showEvidenceModal = true;
      this.modalMode = 'create';
      this.selectedEvidenceData = null;
    },

    handleTableAction(action, index, row, key) {
      if (key === 'editEvidence') {
        const mode = row.approved === true ? 'view' : 'edit';
        this.openEvidenceModal(row.id, mode);
      } else if (key === 'viewFeedback') {
        this.openFeedbackModal(row.id);
      }
      // Download actions handled by k-table-button
    },

    openFeedbackModal(evidenceId) {
      const evidenceItem = this.evidenceData.find(evidence => evidence.project_evidence_id === evidenceId);
      if (evidenceItem) {
        this.setSelectedEvidence(evidenceItem);
        this.showFeedbackModal = true;
        this.$logger.info('Feedback modal opened', { evidenceId });
      } else {
        this.$ktoast.warning('Evidence not found');
      }
    },

    openEvidenceModal(evidenceId, mode) {
      this.evidenceId = evidenceId;
      this.modalMode = mode;

      const evidenceItem = this.evidenceData.find(evidence => evidence.project_evidence_id === evidenceId);
      if (evidenceItem) {
        this.setSelectedEvidence(evidenceItem);
        this.showEvidenceModal = true;
        this.$logger.info('Evidence modal opened', { evidenceId, mode });
      } else {
        this.$ktoast.warning('Evidence not found');
        this.resetModalState();
      }
    },

    setSelectedEvidence(evidenceItem) {
      this.selectedEvidenceData = {
        evidenceId: evidenceItem.project_evidence_id,
        name: evidenceItem.name,
        description: evidenceItem.description,
        competency_mappings: evidenceItem.competency_mappings || [],
        evidence_link: evidenceItem.evidence_link,
        evidence_type: evidenceItem.evidence_type,
        version_id: evidenceItem.version_id,
        file_name: evidenceItem.filename,
        signed_url: evidenceItem.signed_url,
        approved: evidenceItem.approved,
        all_feedback_text: evidenceItem.all_feedback_text,
      };
    },

    handleEvidenceUpdate() {
      this.resetModalState();
      this.$emit('update-evidence-data');
    },

    getEvidenceDownloadBtnConfig(evidence) {
      const { evidenceLink, signedUrl, fileName } = evidence.evidenceUploaded || {};
      return {
        preset: signedUrl ? 'download-file' : 'download-link',
        downloadUrl: signedUrl || evidenceLink,
        title: signedUrl ? `Download ${fileName || 'file'}` : `Open link: ${evidenceLink}`,
        text: 'Download',
        ariaLabel: signedUrl ? 'Download evidence file' : 'Open evidence link',
      };
    },

    getCommentsDownloadBtnConfig(evidence) {
      const signedUrl = evidence.latestMentorComments?.signedUrl;
      return {
        preset: 'download-file',
        downloadUrl: signedUrl,
        title: signedUrl ? `Download mentor comments for ${evidence.name}` : `No mentor comments for ${evidence.name}`,
        text: signedUrl ? 'Download' : 'No mentor comments',
        ariaLabel: signedUrl ? 'Download mentor comments' : 'No mentor comments available',
        disabled: !evidence.latestMentorComments?.signedUrl,
      };
    },
  },
};
</script>
